import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LayoutMode, AvatarShape, PageType } from '../catalogs/pageCatalog.generated';
import pagesService from '../services/pagesService';
import schedulesService from '../services/schedulesService';
import professionalsService from '../services/professionalsService';
import placesService from '../services/placesService';
import servicesService from '../services/servicesService';
import SiteForm from '../components/pages/SiteForm';
import SitePreview from '../components/pages/SitePreview';
import EditorToolbar from '../components/pages/EditorToolbar';
import AppSnackbar from '../components/ui/AppSnackbar';
import useSnackbar from '../hooks/useSnackbar';
import useAutoPreview from '../hooks/useAutoPreview';
import { siteFormSchema } from '../forms/siteFormSchema';
import { usePages } from '../hooks/queries/index';
import normalizeSiteConfig from '../utils/sitePageNormalizer';

/**
 * PageType (int) → string do query param de getTemplates (REST). A API já manda
 * `type` como int (modelo v3); isto serve só pro endpoint de templates, que recebe
 * o tipo como string no query.
 */
function pageTypeToString(t) {
  switch (t) {
    case PageType.Company: return 'company';
    case PageType.Professional: return 'professional';
    default: return 'link_in_bio';
  }
}

// slug da rota (/pages/new/:pageType) → PageType int. Aceita kebab e snake case.
function pageTypeStringToInt(slug) {
  switch ((slug || '').replace(/-/g, '_')) {
    case 'company': return PageType.Company;
    case 'professional': return PageType.Professional;
    default: return PageType.LinkInBio;
  }
}

/**
 * PageEditorPage - Editor de páginas com 2 colunas (form + preview)
 *
 * Suporta:
 * - Criação: /pages/new/link_in_bio (pageType na URL — também aceita 'company' e 'professional')
 * - Edição: /pages/edit/:pageId (carrega dados existentes)
 */
export default function PageEditorPage() {
  const { pageType, pageId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Tipo efetivo da página (PageType enum).
  // Para novas páginas vem da URL (:pageType); para edição é descoberto no loadData.
  const [effectiveType, setEffectiveType] = useState(PageType.LinkInBio);

  // Estados
  const [loading, setLoading] = useState(!!pageId); // Carregar se editando
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [places, setPlaces] = useState([]);
  const [services, setServices] = useState([]);
  const [existingPage, setExistingPage] = useState(null);

  // ── siteForm ──────────────────────────────────────────────────────────────
  const siteForm = useForm({
    resolver: zodResolver(siteFormSchema),
    mode: 'onChange',
    defaultValues: {
      schemaVersion: 3,
      layoutMode: LayoutMode.Continuous,
      seo: { title: '', description: null, ogImageUrl: null },
      customStyles: {},
      identity: { avatarUrl: null, avatarShape: AvatarShape.Round },
      scope: { professionalId: null, placeId: null },
      blocks: [],
    },
  });

  // Ref para SitePreview — expõe refresh() e sendPatch()
  const previewRef = useRef(null);

  // Hook de mutações do React Query para o ciclo draft/publish
  const { createAsync, createSiteAsync, updateDraftAsync, publishPageAsync, isUpdatingDraft, isPublishing } = usePages();

  // Auto-preview: envia patches debounced 150ms para campos estruturais (§9.2)
  useAutoPreview(siteForm, previewRef);

  // Config reativa para manter SitePreview.configRef atualizado (usado pelo refresh())
  const siteFormAllValues = useWatch({ control: siteForm.control });
  const sitePreviewConfig = useMemo(
    () => normalizeSiteConfig(siteFormAllValues),
    [siteFormAllValues]
  );

  // ── Unsaved changes guard ────────────────────────────────────────────────
  // App usa <BrowserRouter> clássico — useBlocker não está disponível.
  // Estratégia: beforeunload para refresh/fechar; safeNavigate para botões
  // explícitos de saída controlados pelo editor (Voltar, breadcrumb).
  const hasUnsavedChanges = siteForm.formState.isDirty;
  const [unsavedDialogTarget, setUnsavedDialogTarget] = useState(null);

  const safeNavigate = useCallback((to) => {
    if (hasUnsavedChanges) {
      setUnsavedDialogTarget(to);
    } else {
      navigate(to);
    }
  }, [hasUnsavedChanges, navigate]);

  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Carregar profissionais, locais e serviços (necessários para escopo dos novos tipos de site)
  useEffect(() => {
    professionalsService.getAll()
      .then((data) => {
        const arr = Array.isArray(data) ? data : (data?.professionals || data?.$values || []);
        setProfessionals(arr);
      })
      .catch(() => {});

    placesService.getAll()
      .then((data) => {
        const arr = Array.isArray(data) ? data : (data?.places || data?.$values || []);
        setPlaces(arr);
      })
      .catch(() => {});

    servicesService.getAll()
      .then((data) => {
        const arr = Array.isArray(data) ? data : (data?.$values || []);
        setServices(arr);
      })
      .catch(() => {});
  }, []);

  // Carregar templates, agendas e página existente
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Carregar templates do tipo de página
      const resolvedType = pageType || 'link_in_bio';
      const templatesResponse = await pagesService.getTemplates(resolvedType);
      setTemplates(Array.isArray(templatesResponse) ? templatesResponse : templatesResponse.$values || []);

      // 2. Carregar agendas do usuário
      try {
        const schedulesResponse = await schedulesService.getAll();
        
        // schedulesService.getAll() already filters e retorna apenas agendas ativas
        // Se for array, use direto. Se for objeto com $values, extrai.
        const schedulesArray = Array.isArray(schedulesResponse) 
          ? schedulesResponse 
          : (schedulesResponse?.$values || []);
        
        setSchedules(schedulesArray);
      } catch (scheduleError) {
        // Não falha totalmente, só deixa agendas vazias
        setSchedules([]);
      }

      // 3. Se criando (não editando), carregar preset do backend
      if (!pageId && pageType) {
        // Tipo efetivo vem da URL na criação (int) — usado no save (CreateSitePageDto.type).
        setEffectiveType(pageTypeStringToInt(pageType));
        try {
          // Normalizar pageType de kebab-case (link-in-bio) para snake_case (link_in_bio)
          const normalizedType = pageType.replace(/-/g, '_');
          const preset = await pagesService.getPreset(normalizedType);
          siteForm.reset({
            schemaVersion: 3,
            layoutMode: preset.layoutMode ?? LayoutMode.Continuous,
            seo: preset.seo ?? {},
            customStyles: preset.customStyles ?? {},
            scope: preset.scope ?? {},
            // Preserva o identity do preset (avatarUrl = HeroImageUrl do tema). O scaffold
            // sobrescreve o avatar quando o profissional escolhido tem foto própria.
            identity: preset.identity ?? { avatarUrl: null, avatarShape: AvatarShape.Round },
            blocks: preset.blocks ?? [],
          });
        } catch (presetErr) {
          console.warn('[PageEditor] Preset failed, using empty defaults', presetErr);
          // Mantém defaults do useForm — não bloqueia
        }
      }

      // 4. Se editando, carregar dados da página
      if (pageId) {
        const pageResponse = await pagesService.getById(pageId);
        setExistingPage(pageResponse);

        // type e templateId vêm do top-level da response — ambos int (modelo v3).
        const pageResponseType = pageResponse.type;             // int (PageType)
        const pageResponseTemplateId = pageResponse.templateId; // int (TemplateId)
        setEffectiveType(pageResponseType);

        // Re-carregar templates (getTemplates recebe o tipo como string no query)
        try {
          const tplResp = await pagesService.getTemplates(pageTypeToString(pageResponseType));
          setTemplates(Array.isArray(tplResp) ? tplResp : tplResp.$values || []);
        } catch { /* mantém templates já carregados */ }

        // ── Inicializar siteForm para edição ──────────────────────────
        const initialConfig = pageResponse.draftConfig || pageResponse.publishedConfig;
        if (initialConfig) {
          siteForm.reset({
            schemaVersion: 3,
            // templateId (int) pré-seleciona o template e habilita o preview
            // (selectedSiteTemplate = templates.find(id === templateId), id é int).
            templateId: pageResponseTemplateId ?? null,
            layoutMode: initialConfig.layoutMode ?? LayoutMode.Continuous,
            seo: initialConfig.seo ?? { title: '', description: null, ogImageUrl: null },
            customStyles: initialConfig.customStyles ?? {},
            identity: initialConfig.identity ?? { avatarUrl: null, avatarShape: AvatarShape.Round },
            scope: {
              professionalId: initialConfig.scope?.professionalId ?? null,
              placeId: initialConfig.scope?.placeId ?? null,
            },
            blocks: initialConfig.blocks ?? [],
          });
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      showError('Erro ao carregar dados do editor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [pageType, pageId]);

  // Estados de carregamento e erro
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const selectedSiteTemplate = templates.find((t) => t.id === siteFormAllValues?.templateId) || null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <EditorToolbar
        form={siteForm}
        pageId={pageId}
        pageType={effectiveType}
        previewRef={previewRef}
        createAsync={createAsync}
        createSiteAsync={createSiteAsync}
        updateDraftAsync={updateDraftAsync}
        publishPageAsync={publishPageAsync}
        isUpdatingDraft={isUpdatingDraft}
        isPublishing={isPublishing}
        showSuccess={showSuccess}
        showError={showError}
        onBack={() => safeNavigate('/pages')}
      />

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Coluna esquerda — SiteForm */}
        <Box
          sx={{
            width: isMobile ? '100%' : '50%',
            overflowY: 'auto',
            p: 2,
            bgcolor: '#fff',
            borderRight: isMobile ? 'none' : '1px solid',
            borderColor: 'divider',
          }}
        >
          <SiteForm
            form={siteForm}
            siteType={effectiveType}
            templates={templates}
            isEdit={!!pageId}
            context={{ availableSchedules: schedules, availablePlaces: places, availableProfessionals: professionals, availableServices: services }}
          />
        </Box>

        {/* Coluna direita — SitePreview */}
        {!isMobile && (
          <Box sx={{ width: '50%', bgcolor: '#f0f0f0' }}>
            <SitePreview
              ref={previewRef}
              template={selectedSiteTemplate}
              config={sitePreviewConfig}
              siteType={effectiveType}
            />
          </Box>
        )}
      </Box>

      {/* Dialog: alterações não salvas */}
      <Dialog open={!!unsavedDialogTarget} onClose={() => setUnsavedDialogTarget(null)}>
        <DialogTitle>Alterações não salvas</DialogTitle>
        <DialogContent>
          Você tem alterações que ainda não foram salvas. Deseja sair mesmo assim?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnsavedDialogTarget(null)}>Continuar editando</Button>
          <Button color="error" onClick={() => { navigate(unsavedDialogTarget); setUnsavedDialogTarget(null); }}>
            Sair sem salvar
          </Button>
        </DialogActions>
      </Dialog>

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </Box>
  );
}
